// ----- T-17 (T17-D2/D14) — usePublishGovernance -----
//
// Publish governance (`preP` / `reAuth`) and the pre-publish checklist items are
// resolved by the BACKEND for ONE submission (its own company profile -> org
// setting -> false) and served by GET /submissions/:id/prepublish-checklist.
// Before this hook the compiler page read two org `useSetting` keys: the wizard
// could disagree with what POST /compile enforced (a submission linked to a
// profile that turns the checklist on was never asked for it), and the item
// list was a second hand-written copy.
//
// FAIL CLOSED: the result is `ready` only after the fetch resolved. While
// `loading` or on `error` the caller must NOT continue the publish flow —
// "could not read the policy" is not "the policy is off".

import { useCallback, useEffect, useRef, useState } from 'react';
import { v2, type PrePublishChecklistResponse } from '@/api/v2Client';

export type UsePublishGovernanceResult =
  | { readonly status: 'loading'; readonly reload: () => void; readonly refresh: () => Promise<void> }
  | {
      readonly status: 'error';
      readonly error: unknown;
      readonly reload: () => void;
      readonly refresh: () => Promise<void>;
    }
  | {
      readonly status: 'ready';
      readonly data: PrePublishChecklistResponse;
      readonly reload: () => void;
      /** Re-fetch WITHOUT leaving `ready` (keeps the user's ticks on screen). */
      readonly refresh: () => Promise<void>;
    };

type Internal =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'ready'; data: PrePublishChecklistResponse };

export function usePublishGovernance(submissionId: string): UsePublishGovernanceResult {
  const [state, setState] = useState<Internal>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  // Latest request wins: a slow earlier response never overwrites a newer one.
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setState({ status: 'loading' });
    v2.submissions
      .getPrePublishChecklist(submissionId)
      .then((data) => {
        if (seq.current === mine) setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (seq.current === mine) setState({ status: 'error', error });
      });
    return () => {
      // Invalidate in-flight work for this effect run (unmount / id change).
      if (seq.current === mine) seq.current += 1;
    };
  }, [submissionId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const refresh = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    try {
      const data = await v2.submissions.getPrePublishChecklist(submissionId);
      if (seq.current === mine) setState({ status: 'ready', data });
    } catch (error: unknown) {
      if (seq.current === mine) setState({ status: 'error', error });
    }
  }, [submissionId]);

  if (state.status === 'ready') return { status: 'ready', data: state.data, reload, refresh };
  if (state.status === 'error') return { status: 'error', error: state.error, reload, refresh };
  return { status: 'loading', reload, refresh };
}
